import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, Crown, Users, X, Settings, CalendarClock, ArrowRight, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/lib/format";
import PlanCard from "@/components/PlanCard";
import { StaggerGroup, StaggerItem } from "@/components/motion/Stagger";
import { motion, useReducedMotion } from "framer-motion";
import { trackCheckoutStarted } from "@/lib/tracking";

const TIER_LABELS = {
  pro_special: "Pro · $1 por 7 días",
  pro_monthly: "Pro · mensual",
  pro_yearly: "Pro · anual",
  family_special: "Familia · $1 por 7 días",
  family_monthly: "Familia · mensual",
  family_yearly: "Familia · anual",
};

export default function BillingSection({ user }) {
  const reduceMotion = useReducedMotion();
  const [params, setParams] = useSearchParams();
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState("monthly"); // special | monthly | yearly
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [pollAttempt, setPollAttempt] = useState(0);
  const POLL_MAX_ATTEMPTS = 5;
  const [subInfo, setSubInfo] = useState(null); // { manageable, next_charge, ... }
  const [showRetention, setShowRetention] = useState(false);

  useEffect(() => {
    api.get("/billing/packages").then(r => setPackages(r.data)).catch(() => {});
  }, []);

  // Manageable recurring subscription info (drives the "Gestionar suscripción"
  // block). Backend `manageable` is authoritative: false for lifetime/comped or
  // any user without a real recurring Stripe subscription.
  const canManage = user?.subscription_active
    && !user?.is_comped
    && user?.subscription_tier !== "lifetime";

  useEffect(() => {
    if (!canManage) return;
    let alive = true;
    api.get("/billing/subscription")
      .then(r => { if (alive) setSubInfo(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [canManage]);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    setPollAttempt(attempt);
    if (attempt >= POLL_MAX_ATTEMPTS) {
      setPolling(false);
      setPollMsg("La verificación tardó más de lo esperado. Refresca esta página en un minuto.");
      return;
    }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPolling(false);
        setPollMsg("");
        toast.success(`¡Pago confirmado! Bienvenido a ${TIER_LABELS[data.package_id] || data.tier}.`);
        params.delete("stripe_session_id");
        setParams(params, { replace: true });
      } else if (data.status === "expired") {
        setPolling(false);
        setPollMsg("La sesión de pago expiró. Puedes intentar nuevamente.");
      } else {
        setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
      }
    } catch {
      setPolling(false);
      setPollMsg("No pudimos verificar el pago. Refresca en un minuto.");
    }
  }, [params, setParams]);

  useEffect(() => {
    const sid = params.get("stripe_session_id");
    const cancelled = params.get("stripe_cancel");
    if (cancelled) {
      toast.info("Cancelaste el pago. Puedes intentarlo de nuevo cuando quieras.");
      params.delete("stripe_cancel");
      setParams(params, { replace: true });
      return;
    }
    if (sid) {
      setPolling(true);
      setPollMsg("Verificando tu pago…");
      setPollAttempt(0);
      pollStatus(sid, 0);
    }
  }, [params, setParams, pollStatus]);

  const startCheckout = async (pkgId) => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      trackCheckoutStarted(pkgId, data.session_id || pkgId, 1);
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e, "No se pudo iniciar el checkout"));
      setLoading(false);
    }
  };

  const isComped = user?.is_comped;
  const isActive = user?.subscription_active || isComped;
  const currentPkg = user?.subscription_package;
  const effectiveTier = user?.effective_tier;

  // Comped users: short-circuit, never show pricing/checkout
  if (isComped) {
    const tierLabel = (effectiveTier || "pro").toUpperCase();
    return (
      <div id="facturacion" className="space-y-4 scroll-mt-20" data-testid="billing-section-comped">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-violet-300/80 font-semibold">Suscripción</div>
          <h2 className="text-xl font-bold mt-1">Tu plan</h2>
        </div>
        <div className="rounded-2xl p-5 bg-gradient-to-br from-violet-500/15 to-fuchsia-500/8 border border-violet-500/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
              <Crown className="w-5 h-5 text-white"/>
            </div>
            <div className="flex-1">
              <div className="font-bold text-base">{tierLabel} · Cortesía</div>
              <div className="text-xs text-slate-400 mt-1">
                Tu cuenta tiene acceso completo otorgado directamente por la administración. No hay cargos, no hay fecha de vencimiento.
              </div>
              {user?.comp_note && (
                <div className="text-[11px] text-slate-500 mt-2 italic">{user.comp_note}</div>
              )}
            </div>
            <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold tracking-wider uppercase">
              Activo
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          ¿Tienes preguntas sobre tu acceso? Escribe a <a href="mailto:soporte@thetradingdock.com" className="text-violet-300 hover:underline">soporte@thetradingdock.com</a>.
        </p>
      </div>
    );
  }

  return (
    <div id="facturacion" className="space-y-5 scroll-mt-20" data-testid="billing-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-violet-300/80 font-semibold">Suscripción</div>
          <h2 className="text-xl font-bold mt-1">{isActive ? "Tu plan" : "Activa tu plan Pro"}</h2>
        </div>
        {isActive && (
          <span className="px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold flex items-center gap-1.5" data-testid="active-badge">
            <Check className="w-3 h-3"/> {TIER_LABELS[currentPkg] || user.subscription_tier} activo
          </span>
        )}
      </div>

      {polling && (
        <div className="rounded-2xl bg-violet-500/10 border border-violet-500/25 px-4 py-3 space-y-2" data-testid="poll-banner">
          <div className="flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-violet-300"/>
            <span className="text-sm text-violet-100">{pollMsg}</span>
          </div>
          <div className="h-1 rounded-full bg-white/10 overflow-hidden" role="progressbar" aria-label="Verificando pago" aria-valuemin={0} aria-valuemax={POLL_MAX_ATTEMPTS} aria-valuenow={pollAttempt}>
            <motion.div
              className="h-full w-full origin-left rounded-full bg-violet-400"
              initial={false}
              animate={{ scaleX: Math.min(1, pollAttempt / POLL_MAX_ATTEMPTS) }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
            />
          </div>
        </div>
      )}
      {!polling && pollMsg && (
        <div className="rounded-2xl bg-rose-500/10 border border-rose-500/25 px-4 py-3 flex items-center justify-between gap-3" data-testid="poll-error">
          <span className="text-sm text-rose-100">{pollMsg}</span>
          <button onClick={() => setPollMsg("")} className="text-rose-200 hover:text-white"><X className="w-4 h-4"/></button>
        </div>
      )}

      {/* Billing cadence toggle */}
      <div className="flex gap-2 p-1 rounded-2xl bg-white/[0.03] border border-white/5 w-fit" data-testid="billing-toggle">
        <ToggleBtn active={billing === "special"} onClick={() => setBilling("special")} testid="bill-special">$1 · 7 días</ToggleBtn>
        <ToggleBtn active={billing === "monthly"} onClick={() => setBilling("monthly")} testid="bill-monthly">Mensual</ToggleBtn>
        <ToggleBtn active={billing === "yearly"} onClick={() => setBilling("yearly")} testid="bill-yearly">Anual · −20%</ToggleBtn>
      </div>

      <StaggerGroup className="grid sm:grid-cols-2 gap-4">
        <StaggerItem className="h-full">
          <PlanCard
            variant="billing"
            icon={Crown}
            tier="pro"
            name="Pro"
            desc="Para uso individual avanzado"
            features={["Cuentas y tarjetas ilimitadas", "OCR de recibos ilimitado", "Asistente del día con IA", "Conciliación bancaria CSV", "USDT P2P en vivo"]}
            amount={packages[`pro_${billing}`]?.amount}
            billing={billing}
            onClick={() => startCheckout(`pro_${billing}`)}
            loading={loading}
            highlight={billing === "special"}
            testid="plan-pro"
            isCurrent={isActive && user?.subscription_tier === "pro"}
          />
        </StaggerItem>
        <StaggerItem className="h-full">
          <PlanCard
            variant="billing"
            icon={Users}
            tier="family"
            name="Familia"
            desc="Hasta 4 usuarios compartidos"
            features={["Todo lo del Pro", "Hasta 4 miembros del hogar", "Presupuestos compartidos", "Reportes consolidados", "Soporte prioritario"]}
            amount={packages[`family_${billing}`]?.amount}
            billing={billing}
            onClick={() => startCheckout(`family_${billing}`)}
            loading={loading}
            badge="Mejor valor"
            testid="plan-family"
            isCurrent={isActive && user?.subscription_tier === "family"}
          />
        </StaggerItem>
      </StaggerGroup>

      {canManage && subInfo?.manageable && (
        <ManageSubscription
          subInfo={subInfo}
          onManage={() => setShowRetention(true)}
        />
      )}

      <p className="text-xs text-slate-500 leading-relaxed" data-testid="billing-legal">
        Pagos procesados de forma segura por Stripe. El cargo aparece en tu estado como <strong className="text-slate-400">Pryma Wellness Group Inc.</strong> (sociedad estadounidense responsable del servicio).
        Puedes cancelar desde tu cuenta en cualquier momento. Si cancelas antes del día 7 del trial, no se te cobra nada más.
      </p>

      {showRetention && (
        <RetentionModal
          onClose={() => setShowRetention(false)}
        />
      )}
    </div>
  );
}

function ManageSubscription({ subInfo, onManage }) {
  const nc = subInfo?.next_charge;
  const dateLabel = nc?.date ? formatChargeDate(nc.date) : null;
  const willCancel = subInfo?.cancel_at_period_end;
  return (
    <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10 space-y-4" data-testid="manage-subscription">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/10 flex items-center justify-center shrink-0">
          <CalendarClock className="w-5 h-5 text-violet-200"/>
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm">
            {willCancel ? "Tu suscripción termina pronto" : "Tu próximo cobro"}
          </div>
          {nc && dateLabel ? (
            <div className="text-xs text-slate-300 mt-1" data-testid="next-charge">
              {willCancel ? (
                <>Tienes acceso hasta el <strong className="text-white">{dateLabel}</strong>. No se te cobrará de nuevo.</>
              ) : (
                <>El <strong className="text-white">{dateLabel}</strong> por <strong className="text-white">${nc.amount.toFixed(2)} {nc.currency}</strong>{nc.interval === "year" ? " / año" : nc.interval === "month" ? " / mes" : ""}.</>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-400 mt-1">
              Gestiona tu método de pago, plan o cancelación cuando quieras.
            </div>
          )}
        </div>
      </div>
      <button
        onClick={onManage}
        data-testid="manage-subscription-cta"
        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm bg-white/10 hover:bg-white/15 text-white transition">
        <Settings className="w-4 h-4"/> Gestionar suscripción
      </button>
    </div>
  );
}

function RetentionModal({ onClose }) {
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [savings, setSavings] = useState(null); // { amount, currency } | null

  useEffect(() => {
    let alive = true;
    api.get("/recurring")
      .then(r => {
        if (!alive) return;
        setSavings(sumSubscriptionSpend(r.data));
      })
      .catch(() => { /* soft: just omit the savings line */ });
    return () => { alive = false; };
  }, []);

  const goToPortal = async () => {
    if (loadingPortal) return;
    setLoadingPortal(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e, "No pudimos abrir el portal de gestión"));
      setLoadingPortal(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog" aria-modal="true" aria-labelledby="retention-title"
      onClick={onClose}
      data-testid="retention-modal">
      <div
        className="w-full max-w-md rounded-3xl bg-[#0f0e17] border border-white/10 p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
            <HeartHandshake className="w-5 h-5 text-white"/>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Cerrar" data-testid="retention-close">
            <X className="w-5 h-5"/>
          </button>
        </div>
        <h3 id="retention-title" className="text-lg font-bold mt-4">Antes de continuar</h3>
        {savings && savings.amount > 0 && (
          <p className="text-sm text-slate-300 mt-2 leading-relaxed" data-testid="retention-savings">
            Este mes llevas registrados <strong className="text-white">${savings.amount.toFixed(2)} {savings.currency}</strong> en suscripciones gracias a Trading Dock.
          </p>
        )}
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          En el portal de Stripe puedes actualizar tu método de pago, cambiar de plan o cancelar. Cancelar mantiene tu acceso hasta el final del período ya pagado.
        </p>
        <div className="mt-6 space-y-2.5">
          <button
            onClick={goToPortal}
            disabled={loadingPortal}
            data-testid="retention-continue"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white transition disabled:opacity-60">
            {loadingPortal ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Continuar a gestionar <ArrowRight className="w-4 h-4"/></>}
          </button>
          <button
            onClick={onClose}
            disabled={loadingPortal}
            data-testid="retention-back"
            className="w-full py-3 rounded-2xl font-semibold text-sm text-slate-300 hover:text-white transition">
            Volver
          </button>
        </div>
      </div>
    </div>
  );
}

function formatChargeDate(epochSeconds) {
  try {
    return new Date(epochSeconds * 1000).toLocaleDateString("es", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return null;
  }
}

// Sum the user's own detected recurring SUBSCRIPTIONS (is_subscription rows),
// grouped by currency; return the dominant currency's total so we never mix
// DOP + USD into a single misleading "$X". Returns null when nothing detected.
function sumSubscriptionSpend(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const byCurrency = {};
  for (const rec of rows) {
    if (!rec?.is_subscription) continue;
    const amt = Number(rec.monthly_amount) || 0;
    if (amt <= 0) continue;
    const cur = rec.currency || "DOP";
    byCurrency[cur] = (byCurrency[cur] || 0) + amt;
  }
  const entries = Object.entries(byCurrency);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return { currency: entries[0][0], amount: entries[0][1] };
}

function ToggleBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${active ? "bg-violet-500 text-white shadow-[0_6px_20px_-6px_rgba(139,92,246,0.6)]" : "text-slate-400 hover:text-white"}`}>
      {children}
    </button>
  );
}
